import React from 'react';
import '../trust-homepage.css';
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Mail,
  MessageCircle,
  Plug,
  UserRoundCheck,
} from 'lucide-react';
import { CONTACT_EMAIL, SUCCESS_STORIES, WHATSAPP_LINK } from '../constants';

const CASE_LINKS = [
  '/blog/caso-exito-seven-sisters-tattoo-madrid.html',
  '/blog/caso-exito-consentirme-estetica-treatwell-whatsapp.html',
] as const;

const CASE_SUMMARIES = [
  'Consentimientos, documentos y recordatorios reunidos en un mismo flujo.',
  'Conversaciones y seguimiento organizados entre Treatwell, Booksy y WhatsApp.',
] as const;

const PROCESS_STEPS = [
  {
    title: 'Atiende y entiende',
    description: 'Responde a tu cliente y recoge la información que necesita para continuar.',
  },
  {
    title: 'Consulta y prepara',
    description: 'Revisa los datos disponibles y prepara la siguiente tarea acordada con tu negocio.',
  },
  {
    title: 'Confirma o pide ayuda',
    description: 'Deja el resultado registrado o pasa el caso a tu equipo con el contexto necesario.',
  },
] as const;

const INTEGRATIONS = [
  { name: 'WhatsApp', logo: '/logos/whatsapp.svg' },
  { name: 'Flowww', logo: '/logos/flowww.svg' },
  { name: 'Treatwell', logo: '/logos/treatwell.svg' },
  { name: 'Booksy', logo: '/logos/booksy.svg' },
  { name: 'TIMP', logo: '/logos/timp.svg' },
  { name: 'Google Calendar', logo: '/logos/googlecalendar.svg' },
] as const;

const USE_CASES = [
  { label: 'Clínicas y centros con agenda', href: '/clinicas-centros-agenda/' },
  { label: 'Estética, belleza y bienestar', href: '/centros-estetica-belleza/' },
  { label: 'Ventas y seguimiento', href: '/agentes-ventas-ia/' },
  { label: 'Software e integraciones', href: '/integraciones-software-ia/' },
] as const;

const BASE_PLAN = [
  'Respuestas a preguntas frecuentes',
  'Recogida de datos del cliente',
  'Solicitudes de cita o reserva',
  'Recordatorios y resumen de actividad',
] as const;

const CONTACT_LINK = `${WHATSAPP_LINK}?text=${encodeURIComponent(
  'Hola, quiero saber si Áncora puede encargarse de una tarea concreta de mi negocio. Necesito ayuda con...',
)}`;

export const TrustHomepageSections: React.FC = () => (
  <>
    <section id="como-gestiona" className="trust-section trust-process" aria-labelledby="trust-process-title">
      <span id="que-hacemos" className="trust-anchor" aria-hidden="true" />
      <span id="how-it-works" className="trust-anchor" aria-hidden="true" />
      <span id="comparativa" className="trust-anchor" aria-hidden="true" />
      <span id="agentes-ventas" className="trust-anchor" aria-hidden="true" />
      <span id="marketing-ia" className="trust-anchor" aria-hidden="true" />
      <div className="marketing-grid trust-process__layout">
        <div className="trust-process__intro">
          <p className="marketing-eyebrow">Qué hace Áncora</p>
          <h2 id="trust-process-title">Entiende la petición y continúa el trabajo</h2>
        </div>
        <ol className="trust-process__steps">
          {PROCESS_STEPS.map((step, index) => (
            <li key={step.title}>
              <span aria-hidden="true">0{index + 1}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="trust-process__control">
          <UserRoundCheck aria-hidden="true" size={22} />
          <p><strong>Tu equipo mantiene el control:</strong> decide los límites y recibe los casos que necesitan criterio.</p>
        </div>
      </div>
    </section>

    <section id="integraciones" className="trust-section trust-integrations" aria-labelledby="trust-integrations-title">
      <span id="hosteleria" className="trust-anchor" aria-hidden="true" />
      <div className="marketing-grid trust-integrations__layout">
        <div className="trust-integrations__copy">
          <p className="marketing-eyebrow">Conectada a tus herramientas</p>
          <h2 id="trust-integrations-title">Trabaja con la información de tu negocio</h2>
          <p>
            Antes de empezar comprobamos cómo se conecta Áncora con WhatsApp, tu agenda, tu CRM o
            tu software. Así sabrás qué información puede consultar y qué tareas puede realizar.
          </p>
          <a className="trust-text-link" href="/integraciones-software-ia/">
            Ver conexiones disponibles <ArrowRight aria-hidden="true" size={17} />
          </a>
        </div>
        <div className="trust-integrations__logos" aria-label="Herramientas que revisamos según disponibilidad">
          {INTEGRATIONS.map((integration) => (
            <div key={integration.name}>
              <img src={integration.logo} alt="" loading="lazy" />
              <span>{integration.name}</span>
            </div>
          ))}
          <p><Plug aria-hidden="true" size={18} /> ¿Utilizas otra herramienta? Comprobamos si se puede conectar.</p>
        </div>
      </div>

      <nav className="marketing-grid trust-use-cases" aria-label="Soluciones por tipo de negocio">
        {USE_CASES.map((useCase) => (
          <a key={useCase.href} href={useCase.href}>
            {useCase.label} <ArrowUpRight aria-hidden="true" size={15} />
          </a>
        ))}
      </nav>
    </section>

    <section id="casos" className="trust-section trust-proof" aria-labelledby="trust-proof-title">
      <span id="caso-destacado" className="trust-anchor" aria-hidden="true" />
      <div className="marketing-grid trust-heading">
        <p className="marketing-eyebrow">Casos reales</p>
        <h2 id="trust-proof-title">Así ayuda Áncora en negocios reales</h2>
      </div>

      <div className="marketing-grid trust-cases">
        {SUCCESS_STORIES.map((story, index) => (
          <article className="trust-case" key={story.name}>
            <a className="trust-case__media" href={CASE_LINKS[index]} aria-label={`Conocer el caso de ${story.name}`}>
              <img src={(story.images ?? [story.image])[0]} alt={`${story.name}, ${story.location}`} loading="lazy" />
            </a>
            <div className="trust-case__copy">
              <div className="trust-case__meta">
                <span>Caso real</span>
                <span>{story.location}</span>
              </div>
              <h3>{story.name}</h3>
              <p className="trust-case__summary">{CASE_SUMMARIES[index]}</p>
              <blockquote>
                <p>“{story.quote}”</p>
                <cite>{story.quoteAuthor}</cite>
              </blockquote>
              <a className="trust-text-link" href={CASE_LINKS[index]} data-marketing-event="case_click">
                Ver el caso completo <ArrowUpRight aria-hidden="true" size={17} />
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>

    <section id="pricing" className="trust-section trust-scope" aria-label="Planes y precios">
      <div className="marketing-grid trust-scope__panel">
        <article className="trust-scope__summary">
          <p className="trust-scope__label">Plan inicial</p>
          <h3>Atención básica por WhatsApp</h3>
          <p className="trust-scope__price">
            <span>Desde</span>
            <strong>9,99 €/mes</strong>
          </p>
        </article>
        <article className="trust-scope__included">
          <p className="trust-scope__label">Incluye</p>
          <h3>Qué incluye</h3>
          <ul>
            {BASE_PLAN.map((item) => <li key={item}><Check aria-hidden="true" size={17} /> {item}</li>)}
          </ul>
        </article>
        <article className="trust-scope__extras">
          <p className="trust-scope__label">Conexiones y tareas adicionales</p>
          <h3>¿Necesitas algo más? Lo estudiamos contigo</h3>
          <p>Dinos qué herramienta o tarea quieres añadir y prepararemos una propuesta clara.</p>
          <a className="marketing-button marketing-button--primary" href={CONTACT_LINK} target="_blank" rel="noreferrer">
            Pedir un presupuesto <ArrowRight aria-hidden="true" size={17} />
          </a>
        </article>
      </div>
    </section>

    <section id="contacto" className="trust-section trust-contact" aria-labelledby="trust-contact-title">
      <div className="marketing-grid trust-contact__layout">
        <div className="trust-contact__copy">
          <p className="marketing-eyebrow">Hablemos</p>
          <h2 id="trust-contact-title">Cuéntanos qué necesitas</h2>
          <p>Te diremos cómo puede ayudarte Áncora y cuál es el siguiente paso.</p>
          <div className="trust-contact__actions">
            <a className="marketing-button marketing-button--primary" href={CONTACT_LINK} target="_blank" rel="noreferrer">
              <MessageCircle aria-hidden="true" size={18} /> Escribir por WhatsApp
            </a>
            <a className="marketing-button marketing-button--secondary" href={`mailto:${CONTACT_EMAIL}`}>
              <Mail aria-hidden="true" size={18} /> Enviar un correo
            </a>
          </div>
        </div>
      </div>
    </section>
  </>
);
